import Immutable from 'immutable';
import {initialDesignId} from '~/utils/initializer';
import {RECENT_UPLOADS_MAX} from '~/constants/CanvasConstants';
// types
import ArtTypes from '~/constants/types/ArtTypes';
import CanvasTypes from '~/constants/types/CanvasTypes';
import DesignTypes from '~/constants/types/DesignTypes';
import IconTypes from '~/constants/types/IconTypes';
import NamesAndNumbersTypes from '~/constants/types/NamesAndNumbersTypes';
import ProductTypes from '~/constants/types/ProductTypes';
import SelectionTypes from '~/constants/types/SelectionTypes';
import TextTypes from '~/constants/types/TextTypes';
import UploadTypes from '~/constants/types/UploadTypes';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {rearViewPresent} from '~/utils/url';
import {makeUndoable} from '~/utils/undoable';

// data helpers
import {
  isNames,
  isNamesOrNumbers,
  isNumbers,
  isText
} from '~/data/accessors/icon';

const excludeIds = targetIds => testId => !targetIds.includes(testId);

export function initialState() {
  return Immutable.Map({
    clipboard: Immutable.List(),
    clipboardSource: null,
    currentDesignId: initialDesignId,
    disabled: false,
    disableArrowKeys: false,
    labBlankLoad: false,
    lastArtIconId: null,
    lastIconId: null,
    lastNamesIconId: null,
    lastNumbersIconId: null,
    lastTextIconId: null,
    lastUploadIconId: null,
    recentUploadIconIds: Immutable.OrderedSet(),
    selectedIconIds: Immutable.List(),
    selectedViewId: rearViewPresent() ? VIEW_ID_MAP.back : VIEW_ID_MAP.front,
    sleevePrintEnabled: false,
    productColorRemovalThreshold: 9
  });
}

export function canvas(state = initialState(), action) {
  switch (action.type) {
    case CanvasTypes.SET_LAB_BLANK_LOAD:
      return state.set('labBlankLoad', action.payload);

    case CanvasTypes.SET_DISABLE_ARROW_KEYS:
      return state.set('disableArrowKeys', action.payload);

    case CanvasTypes.ACTIVATE_SLEEVE_PRINT:
      return state.set('sleevePrintEnabled', action.payload);

    case CanvasTypes.CHANGE_THRESHOLD:
      return state.set('productColorRemovalThreshold', action.payload);

    case CanvasTypes.CLEAR_RECENT_UPLOADS:
      return state.set('recentUploadIconIds', Immutable.OrderedSet());

    case CanvasTypes.REMOVE_RECENT_UPLOAD:
      return state.update('recentUploadIconIds', iconIds =>
        iconIds.delete(action.payload)
      );

    case ArtTypes.ADD_ART:
      return state.set('lastArtIconId', action.payload.iconId);

    case ArtTypes.UPDATE_LAST_ART:
      return state.set('lastArtIconId', action.payload?.iconId);

    case CanvasTypes.CHANGE_VIEW: {
      return state.set('selectedViewId', action.payload);
    }

    case CanvasTypes.CLEAR: {
      return initialState();
    }

    case DesignTypes.CLEAR_DESIGN_DATA: {
      return state.merge({
        labBlankLoad: true,
        currentDesignId: initialDesignId
      });
    }

    case CanvasTypes.DISABLE: {
      const {disabled} = action.payload;
      return state.set('disabled', disabled);
    }

    case CanvasTypes.SHOW_DESIGN: {
      const {designId} = action.payload;
      return state.set('currentDesignId', designId);
    }

    case IconTypes.CHANGE_COLOR: {
      return state.set('lastIconId', action.payload.iconId);
    }

    case IconTypes.COPY: {
      const {iconsMap, viewId} = action.payload;
      const copyableItems = iconsMap.filter(
        item => !isNamesOrNumbers(item.sourceIconType)
      );

      if (!copyableItems.length) {
        return state;
      }

      return state
        .set(
          'clipboard',
          Immutable.List(copyableItems.map(item => item.targetIconId))
        )
        .set('clipboardSource', viewId);
    }

    case IconTypes.CUT: {
      const {iconsMap, viewId} = action.payload;
      const copyableItems = iconsMap.filter(
        item => !isNamesOrNumbers(item.sourceIconType)
      );

      if (!copyableItems.length) {
        return state;
      }

      return state
        .set(
          'clipboard',
          Immutable.List(copyableItems.map(item => item.targetIconId))
        )
        .set('clipboardSource', viewId)
        .update('selectedIconIds', iconIds =>
          iconIds.filter(
            icon => !copyableItems.find(item => item.sourceIconId !== icon.id)
          )
        );
    }

    case IconTypes.DUPLICATE:
    case IconTypes.PASTE: {
      const {iconsMap} = action.payload;

      return state.set(
        'selectedIconIds',
        Immutable.List(iconsMap.map(item => item.targetIconId))
      );
    }

    case IconTypes.RESIZE: {
      const {transforms} = action.payload;

      transforms.forEach(({icon}) => {
        if (isText(icon.type)) {
          state = state.set('lastTextIconId', icon.id);
        }
      });

      return state;
    }

    case IconTypes.RESTORE:
      return state.set(
        'selectedIconIds',
        Immutable.List([action.payload.targetIconId])
      );
    case IconTypes.ORDER_RECENT_UPLOADS: {
      const {sourceIconId} = action.payload;
      return state.update('recentUploadIconIds', recentUploadIconIds => {
        return recentUploadIconIds
          .delete(sourceIconId)
          .add(sourceIconId)
          .takeLast(RECENT_UPLOADS_MAX);
      });
    }

    case IconTypes.REMOVE: {
      const {iconIds} = action.payload;
      return state.updateIn(['selectedIconIds'], currentIconIds =>
        currentIconIds.filter(excludeIds(iconIds))
      );
    }

    case IconTypes.SET_VIEW: {
      const {iconId, iconType} = action.payload;
      if (isNames(iconType)) {
        return state.set('lastNamesIconId', iconId);
      } else if (isNumbers(iconType)) {
        return state.set('lastNumbersIconId', iconId);
      }
      return state;
    }

    case NamesAndNumbersTypes.ADD_NAMES: {
      const {iconId, viewId} = action.payload;
      return state.merge({
        lastNamesIconId: iconId,
        selectedViewId: viewId
      });
    }

    case NamesAndNumbersTypes.ADD_NUMBERS: {
      const {iconId, viewId} = action.payload;
      return state.merge({
        lastNumbersIconId: iconId,
        selectedViewId: viewId
      });
    }

    case NamesAndNumbersTypes.ADD_NAMES_AND_NUMBERS: {
      const {viewId, namesIconId, numbersIconId} = action.payload;
      return state.merge({
        lastNamesIconId: namesIconId,
        lastNumbersIconId: numbersIconId,
        selectedViewId: viewId
      });
    }

    case NamesAndNumbersTypes.CLEAR: {
      return state.merge({
        lastNamesIconId: null,
        lastNumbersIconId: null
      });
    }

    case ProductTypes.SWAP_PRODUCT: {
      const {views} = action.payload;
      if (!views) {
        return state;
      }
      return state.set('selectedViewId', views[0].id);
    }

    case SelectionTypes.DESELECT_ALL_ICONS: {
      return state.set('selectedIconIds', Immutable.List());
    }

    case SelectionTypes.DESELECT_ICON: {
      return state.set(
        'selectedIconIds',
        state
          .getIn(['selectedIconIds'])
          .filter(iconId => iconId !== action.payload.iconId)
      );
    }

    case SelectionTypes.SELECT_ICONS: {
      const {iconIds, viewId} = action.payload;
      const newState = {
        selectedIconIds: Immutable.List(iconIds)
      };
      if (viewId) {
        newState.selectedViewId = viewId;
      }
      return state.merge(newState);
    }

    case SelectionTypes.SELECT_ADDITIONAL_ICONS: {
      return state.set(
        'selectedIconIds',
        state.getIn(['selectedIconIds']).concat(action.payload.iconId)
      );
    }

    case SelectionTypes.SELECT_ICON: {
      return state.set(
        'selectedIconIds',
        Immutable.List().concat(action.payload.iconId)
      );
    }

    case TextTypes.UPDATE_LAST_TEXT: {
      return state.set('lastTextIconId', action.payload.iconId);
    }
    case TextTypes.ADD_TEXT:
    case TextTypes.UPDATE_FONT:
    case TextTypes.UPDATE_OUTLINE:
    case TextTypes.UPDATE_EFFECT: {
      return state.set('lastTextIconId', action.payload.iconId);
    }

    case TextTypes.UPDATE_FONT_HEIGHT: {
      const {iconId, iconType} = action.payload;
      if (isNames(iconType)) {
        return state.set('lastNamesIconId', iconId);
      } else if (isNumbers(iconType)) {
        return state.set('lastNumbersIconId', iconId);
      } else {
        return state.set('lastTextIconId', iconId);
      }
    }

    case UploadTypes.UPLOAD_COMPLETE: {
      const {iconId, recentUploadCopyId} = action.payload;
      return state
        .set('lastUploadIconId', iconId)
        .update('recentUploadIconIds', recentUploadIconIds => {
          return recentUploadIconIds
            .add(recentUploadCopyId)
            .takeLast(RECENT_UPLOADS_MAX);
        });
    }

    case UploadTypes.UPDATE_LAST_UPLOAD: {
      return state.set('lastUploadIconId', action.payload?.iconId);
    }

    case UploadTypes.ADD_RECENT_UPLOAD: {
      const {recentUploadCopyId} = action.payload;
      return state.update('recentUploadIconIds', recentUploadIconIds => {
        return recentUploadIconIds
          .add(recentUploadCopyId)
          .takeLast(RECENT_UPLOADS_MAX);
      });
    }

    default:
      return state;
  }
}

export default makeUndoable(canvas);
